import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

const getDocumentTheme = (): ThemeMode | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const root = document.documentElement;
  if (root.classList.contains('dark')) {
    return 'dark';
  }
  if (root.classList.contains('light')) {
    return 'light';
  }

  return undefined;
};

const getSystemTheme = (): ThemeMode => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export function useResolvedTheme(): ThemeMode {
  const [theme, setTheme] = useState<ThemeMode>(() => getDocumentTheme() ?? getSystemTheme());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const updateTheme = () => {
      setTheme(getDocumentTheme() ?? getSystemTheme());
    };

    updateTheme();

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleMediaChange = () => updateTheme();
    media.addEventListener('change', handleMediaChange);

    const observer =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => updateTheme())
        : null;
    if (observer) {
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }

    return () => {
      media.removeEventListener('change', handleMediaChange);
      observer?.disconnect();
    };
  }, []);

  return theme;
}
